import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Transaction } from '@mysten/sui/transactions'
import { useCurrentClient } from '@mysten/dapp-kit-react'
import { QueryObserverResult, useQueryClient } from '@tanstack/react-query'

import { Typography } from 'components/Typography/Typography'
import { Input } from 'components/Input/Input'
import { refetchWalletBalances, useWalletBalances } from '@/shared/hooks/useWalletBalances'
import { useInputAmountValidate } from '@/shared/hooks/useInputAmountValidate'
import { Button } from 'components/Button/Button'
import { Spinner } from 'components/Spinner/Spinner'
import { useVaultInfo, VaultInfo } from '@/shared/kai/hooks/useVaultInfo'
import { useUserVaultInfo } from 'utils/useUserVaultInfo'
import { frozenVaults, VaultDisplayInfo } from 'utils/vaultDisplayInfo'
import { useIsMobile } from 'utils/useIsMobile'
import { PhantomTypeArgument } from '@/shared/gen/_framework/reified'
import { calcContinuousApy } from '@/shared/util'
import { Amount, findVaultNameById, Price, round, SUI, USDC } from '@kunalabs-io/kai'
import { useTokenPrice } from 'utils/useTokenPrice'
import { GetActivityHistoryResponseType } from '@/shared/backend/types'

import { refetchObjectData } from '@/shared/hooks/batshitObjectFetcher'
import Decimal from 'decimal.js'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { IconInfo } from 'components/Icons/InfoIcon'
import { usePostHogEntityAction } from 'utils/usePostHogEntityAction'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'
import { SAV_DEPOSIT, SAV_WITHDRAW } from 'utils/sentry-utils'
import { useExecute } from 'utils/useExecute'

interface Props {
  vaultDisplayInfo: VaultDisplayInfo
  vaultInfo: VaultInfo<PhantomTypeArgument, PhantomTypeArgument>
  maxWithdrawable?: Amount
  refetchSavActivityHistory: () => Promise<QueryObserverResult<GetActivityHistoryResponseType, Error>>
}

const Container = styled.div`
  border-radius: 16px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.background.primary};
  box-shadow: 0px 0px 16px 0px ${p => p.theme.colors.background.hover};
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-bottom: 20px;
  }
  height: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: center;
`

const Tab = styled(Typography)<{ $isActive: boolean }>`
  cursor: pointer;
  text-align: center;
  padding: 16px 32px;
  cursor: pointer;
  color: ${p => p.theme.colors.text.primary};
  border-bottom: 2px solid ${p => (p.$isActive ? p.theme.colors.text.primary : p.theme.colors.inactiveTab)};
  font-size: ${p => p.theme.typography.size.Label1}px;
  font-weight: ${p => (p.$isActive ? p.theme.typography.weight.Bold : p.theme.typography.weight.Medium)};
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Description}px;
  }
`

const Content = styled.div`
  padding: 24px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding: 20px 10px;
  }
`

const Token = styled(Typography)`
  color: ${p => p.theme.colors.text.primaryDark};
  margin-left: 8px;
`

const InputLabel = styled(Typography)`
  color: ${p => p.theme.colors.text.secondary};
  margin-bottom: 16px;
`

const InputLabelWithTooltip = styled(Typography)`
  color: ${p => p.theme.colors.text.secondary};
  margin-bottom: 16px;
  display: flex;
  align-items: center;

  & > div {
    display: flex;
    cursor: pointer;
    align-items: center;
    & > svg {
      margin-left: 6px;
      width: 16px;
      height: 16px;
      & > path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

const InputContainer = styled.div`
  display: flex;
  padding: 24px 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.background.secondary};
`

const MaxInfo = styled.div`
  margin-left: auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 8px;
`

const Available = styled(Typography)`
  color: ${p => p.theme.colors.text.secondary};
  margin-right: 8px;
`

const SubmitButton = styled(Button)`
  margin-top: 20px;
  width: 100%;
  font-weight: 700;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 48px;
`

const ProfitSimulationContainer = styled.div`
  margin: 40px 0;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-top: 24 0px;
  }
`

const ProfitSimulationCardContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const ProfitSimulationCard = styled.div`
  background-color: ${p => p.theme.colors.background.secondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  padding: 16px 0px;
  text-align: center;
  width: 30%;
  gap: 16px;
  & > div {
    overflow: hidden;
  }
`

const DEPOSIT = 0
const WITHDRAW = 1

export const getEstimateValue = (amount?: Amount, price?: Price<PhantomTypeArgument, PhantomTypeArgument>) => {
  if (!amount || !price) {
    return ''
  }

  const value = amount.toNumber() * price.human.toNumber()

  return `~ $${round(value, 2).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
}

export const DepositWithdrawCard = ({
  vaultDisplayInfo,
  vaultInfo,
  maxWithdrawable,
  refetchSavActivityHistory,
}: Props) => {
  const { capture } = usePostHogEntityAction()

  const [activeTab, setActiveTab] = useState(DEPOSIT)

  const handleTabClick = useCallback((value: number) => {
    setActiveTab(value)
  }, [])

  const walletAddress = useCurrentWalletAddress()
  const isConnected = !!walletAddress

  const client = useCurrentClient()
  const execute = useExecute()

  const vault = vaultDisplayInfo.vault

  const { price } = useTokenPrice(vault.T, USDC)

  const balances = useWalletBalances({
    refetchInterval: 5000,
  }).infos
  const tBalance = balances?.get(vault.T.typeName)

  const queryClient = useQueryClient()

  // vault user info
  const userVaultInfo = useUserVaultInfo(vault)
  const refetchVaultAndWalletData = useCallback(async () => {
    await Promise.all([refetchObjectData(queryClient, vault.id), refetchWalletBalances(queryClient, walletAddress)])
  }, [vault.id, walletAddress, queryClient])

  // tvl limit
  const { tvlCap, tvl } = useVaultInfo(vault, { refetchInterval: 5000 })
  const isTvlCapReached = tvlCap && tvl && tvl.int >= tvlCap.int

  const isFrozenVault = frozenVaults.some(fv => fv.vault.id === vault.id)

  // deposit
  const [depositRawInputStr, setDepositRawInputStr] = useState('')

  const { sanitizedInputValue: sanitizedDepositValue, amount: depositAmount } = useInputAmountValidate(
    depositRawInputStr,
    vault.T.decimals
  )

  const handleDepositChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDepositRawInputStr(e.target.value)
  }, [])

  const [depositIsLoading, setDepositIsLoading] = useState(false)

  const handleDepositClick = useCallback(async () => {
    if (!walletAddress || !depositAmount || depositIsLoading) {
      return
    }

    setDepositIsLoading(true)

    void execute(
      async () => {
        const tx = new Transaction()
        await vault.depositFromWallet(tx, walletAddress, depositAmount)
        return tx
      },
      {
        transactionType: SAV_DEPOSIT,
        sentryContext: {
          walletAddress,
          inputValue: sanitizedDepositValue,
          vaultId: vault.id,
        },
        onSuccess: async () => {
          capture('sav:vault_deposit', {
            entity_id: vault.id,
            entity_name: findVaultNameById(vault.id),
            amount_usd: price ? Math.round(depositAmount.toNumber() * price.human.toNumber() * 100) / 100 : null,
          })
          setDepositRawInputStr('')
          void refetchVaultAndWalletData()
          setDepositIsLoading(false)
        },
        onProcessed: async () => {
          await refetchSavActivityHistory()
        },
        onError: () => {
          setDepositIsLoading(false)
        },
      }
    )
  }, [
    walletAddress,
    depositAmount,
    price,
    capture,
    execute,
    refetchVaultAndWalletData,
    vault,
    depositIsLoading,
    sanitizedDepositValue,
    refetchSavActivityHistory,
  ])

  const handleMaxDepositClick = useCallback(() => {
    let value: string
    if (!tBalance) {
      value = '0'
    } else if (vault.T.typeName === SUI.typeName) {
      value = Decimal.max(tBalance.toDecimal().sub(1), 0).toString()
    } else {
      value = tBalance.toString()
    }

    setDepositRawInputStr(value)
  }, [tBalance, vault.T.typeName])

  let depositAmountError = false
  if (!depositAmount || depositAmount.int == 0n || !tBalance || depositAmount.int > tBalance.int) {
    depositAmountError = true
  }

  // withdraw
  const [withdrawRawInputStr, setWithdrawRawInputStr] = useState('')

  const { sanitizedInputValue: sanitizedWithdrawValue, amount: withdrawAmount } = useInputAmountValidate(
    withdrawRawInputStr,
    vault.YT.decimals
  )

  const handleWithdrawChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setWithdrawRawInputStr(e.target.value)
  }, [])

  const [withdrawIsLoading, setWithdrawIsLoading] = useState(false)

  const handleWithdrawClick = useCallback(async () => {
    if (!walletAddress || !withdrawAmount || withdrawIsLoading) {
      return
    }

    setWithdrawIsLoading(true)

    void execute(
      async () => {
        const tx = new Transaction()
        const strategies = vault.getStrategies()
        if (userVaultInfo.equity && withdrawAmount.int === userVaultInfo.equity.int) {
          await vault.withdrawToWalletAll(client, tx, walletAddress, strategies)
        } else {
          await vault.withdrawToWalletT(client, tx, walletAddress, withdrawAmount, strategies)
        }
        return tx
      },
      {
        transactionType: SAV_WITHDRAW,
        sentryContext: {
          walletAddress,
          inputValue: sanitizedWithdrawValue,
          vaultId: vault.id,
        },
        onSuccess: async () => {
          capture('sav:vault_withdraw', {
            entity_id: vault.id,
            entity_name: findVaultNameById(vault.id),
            amount_usd: price ? Math.round(withdrawAmount.toNumber() * price.human.toNumber() * 100) / 100 : null,
          })
          setWithdrawRawInputStr('')
          void refetchVaultAndWalletData()
          setWithdrawIsLoading(false)
        },
        onProcessed: async () => {
          await refetchSavActivityHistory()
        },
        onError: () => {
          setWithdrawIsLoading(false)
        },
      }
    )
  }, [
    withdrawIsLoading,
    walletAddress,
    withdrawAmount,
    price,
    capture,
    client,
    execute,
    refetchVaultAndWalletData,
    vault,
    userVaultInfo.equity,
    sanitizedWithdrawValue,
    refetchSavActivityHistory,
  ])

  const handleMaxWithdrawClick = useCallback(() => {
    if (maxWithdrawable && userVaultInfo.equity && userVaultInfo.equity.int > maxWithdrawable.int) {
      setWithdrawRawInputStr(maxWithdrawable.toString())
      return
    }
    const value = userVaultInfo.equity ? userVaultInfo.equity.toString() : '0'
    setWithdrawRawInputStr(value)
  }, [maxWithdrawable, userVaultInfo.equity])

  let withdrawAmountError = false
  if (
    !withdrawAmount ||
    withdrawAmount.int == 0n ||
    !userVaultInfo.equity ||
    withdrawAmount.int > userVaultInfo.equity.int ||
    (maxWithdrawable && withdrawAmount.int > maxWithdrawable?.int)
  ) {
    withdrawAmountError = true
  }

  const isMobile = useIsMobile()

  const profitSimulationResult = useMemo(() => {
    const amount = depositAmount || withdrawAmount
    if (!vaultInfo.apr || amount === undefined) {
      return undefined
    }

    return {
      days1: calcContinuousApy(vaultInfo.apr, 1) * amount.toNumber(),
      days7: calcContinuousApy(vaultInfo.apr, 7) * amount.toNumber(),
      days30: calcContinuousApy(vaultInfo.apr, 30) * amount.toNumber(),
    }
  }, [vaultInfo.apr, depositAmount, withdrawAmount])

  useEffect(() => {
    setDepositRawInputStr('')
    setWithdrawRawInputStr('')
  }, [vaultDisplayInfo])

  return (
    <Container>
      <Header>
        <div onClick={() => handleTabClick(DEPOSIT)}>
          <Tab $isActive={activeTab === DEPOSIT} variant="label1" fontWeight="bold">
            Deposit
          </Tab>
        </div>
        <div onClick={() => handleTabClick(WITHDRAW)}>
          <Tab $isActive={activeTab === WITHDRAW} variant="label1" fontWeight="bold">
            Withdraw
          </Tab>
        </div>
      </Header>
      <Content>
        <InputLabel variant="body">Enter amount</InputLabel>
        <InputContainer>
          <div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <vaultDisplayInfo.icon />
              <Token variant="label2" fontWeight="bold">
                {vaultDisplayInfo.tSymbol}
              </Token>
            </div>
            {isConnected &&
              (activeTab === DEPOSIT ? (
                <MaxInfo>
                  <Available variant="caption">
                    Available:&nbsp;
                    {tBalance ? tBalance.toNumber().toLocaleString('en-US', { maximumFractionDigits: 20 }) : 0}
                  </Available>
                  <div onClick={handleMaxDepositClick}>
                    <Typography variant="body" fontWeight="bold" style={{ cursor: 'pointer' }}>
                      Max
                    </Typography>
                  </div>
                </MaxInfo>
              ) : (
                <MaxInfo>
                  <Available variant="caption">
                    Available:&nbsp;
                    {userVaultInfo.equity
                      ? userVaultInfo.equity.toNumber().toLocaleString('en-US', { maximumFractionDigits: 20 })
                      : 0}
                  </Available>
                  <div onClick={handleMaxWithdrawClick}>
                    <Typography variant="body" fontWeight="bold" style={{ cursor: 'pointer' }}>
                      Max
                    </Typography>
                  </div>
                </MaxInfo>
              ))}
          </div>
          <div>
            {activeTab === DEPOSIT ? (
              <Input
                placeholder="0.00"
                inputText={sanitizedDepositValue}
                onChange={handleDepositChange}
                currency={vaultDisplayInfo.tSymbol}
                disabled={!isConnected}
                estimatedValue={getEstimateValue(depositAmount, price)}
                // style={{ marginBottom: 12 }}
              />
            ) : (
              <Input
                placeholder="0.00"
                inputText={sanitizedWithdrawValue}
                onChange={handleWithdrawChange}
                currency={vaultDisplayInfo.tSymbol}
                disabled={!isConnected}
                estimatedValue={getEstimateValue(withdrawAmount, price)}
                // style={{ marginBottom: 12 }}
              />
            )}
          </div>
        </InputContainer>
        <ProfitSimulationContainer>
          <InputLabelWithTooltip variant="body">
            Profit Simulation
            <Tooltip
              content="Shows how much yield you’d earn or miss based on your deposit or withdrawal."
              position="top"
            >
              <IconInfo />
            </Tooltip>
          </InputLabelWithTooltip>
          <ProfitSimulationCardContainer>
            <ProfitSimulationCard>
              <Typography variant={isMobile ? 'caption' : 'body'} color="secondary" style={{ marginBottom: 8 }}>
                1 Day
              </Typography>
              <Typography variant={isMobile ? 'description' : 'subtitle2'} fontWeight="bold">
                {profitSimulationResult && activeTab === WITHDRAW ? '-' : ''}{' '}
                {profitSimulationResult ? vaultDisplayInfo.formatAmount(profitSimulationResult.days1) : '-'}
              </Typography>
            </ProfitSimulationCard>
            <ProfitSimulationCard>
              <Typography variant={isMobile ? 'caption' : 'body'} color="secondary" style={{ marginBottom: 8 }}>
                7 Days
              </Typography>
              <Typography variant={isMobile ? 'description' : 'subtitle2'} fontWeight="bold">
                {profitSimulationResult && activeTab === WITHDRAW ? '-' : ''}{' '}
                {profitSimulationResult ? vaultDisplayInfo.formatAmount(profitSimulationResult.days7) : '-'}
              </Typography>
            </ProfitSimulationCard>
            <ProfitSimulationCard>
              <Typography variant={isMobile ? 'caption' : 'body'} color="secondary" style={{ marginBottom: 8 }}>
                30 Days
              </Typography>
              <Typography variant={isMobile ? 'description' : 'subtitle2'} fontWeight="bold">
                {profitSimulationResult && activeTab === WITHDRAW ? '-' : ''}{' '}
                {profitSimulationResult ? vaultDisplayInfo.formatAmount(profitSimulationResult.days30) : '-'}
              </Typography>
            </ProfitSimulationCard>
          </ProfitSimulationCardContainer>
        </ProfitSimulationContainer>
        {activeTab === DEPOSIT ? (
          <SubmitButton
            disabled={!isConnected || !sanitizedDepositValue || depositAmountError || isTvlCapReached || isFrozenVault}
            onClick={handleDepositClick}
          >
            {!depositIsLoading && 'Deposit'}
            {depositIsLoading && <Spinner style={{ marginTop: 0, marginLeft: 'inherit' }} />}
          </SubmitButton>
        ) : (
          <SubmitButton
            disabled={!isConnected || !sanitizedWithdrawValue || withdrawAmountError}
            onClick={handleWithdrawClick}
          >
            {!withdrawIsLoading && 'Withdraw'}
            {withdrawIsLoading && <Spinner style={{ margin: 0, marginLeft: 'inherit' }} />}
          </SubmitButton>
        )}
      </Content>
    </Container>
  )
}
